import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, FileText, Zap, AlertCircle, CheckCircle2, Code } from 'lucide-react';

const JsonEscapeUnescapeTool = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [mode, setMode] = useState('escape'); // 'escape' or 'unescape'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [stats, setStats] = useState({
    charactersProcessed: 0,
    escapesFound: 0,
    processingTime: 0,
    operationsCount: 0,
    isValidJson: false
  });
  const [notification, setNotification] = useState('');

  // JSON escape sequences map
  const jsonEscapes = {
    '"': '\\"',
    '\\': '\\\\',
    '/': '\\/',
    '\b': '\\b',
    '\f': '\\f',
    '\n': '\\n',
    '\r': '\\r',
    '\t': '\\t'
  };

  const escapeJson = useCallback((text: any) => {
    const startTime = performance.now();
    let result = text;
    let escapesCount = 0;

    // Escape special characters
    Object.entries(jsonEscapes).forEach(([char, escape]) => {
      const regex = new RegExp(char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const matches = result.match(regex);
      if (matches) {
        escapesCount += matches.length;
        result = result.replace(regex, escape);
      }
    });

    // Escape unicode characters (optional for readability)
    result = result.replace(/[\u0000-\u001F\u007F-\u009F]/g, (match: any) => {
      escapesCount++;
      return '\\u' + ('0000' + match.charCodeAt(0).toString(16)).slice(-4);
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        escapesFound: escapesCount,
        processingTime: endTime - startTime,
        isValidJson: false // Escaped text is not valid JSON by itself
      }
    };
  }, []);

  const unescapeJson = useCallback((text: any) => {
    const startTime = performance.now();
    let result = text;
    let escapesCount = 0;
    let isValidJson = false;

    try {
      // First try to parse as JSON to validate
      JSON.parse(result);
      isValidJson = true;
    } catch (e) {
      // If not valid JSON, try to unescape anyway
    }

    // Unescape JSON escape sequences
    Object.entries(jsonEscapes).forEach(([char, escape]) => {
      const regex = new RegExp(escape.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const matches = result.match(regex);
      if (matches) {
        escapesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    // Unescape unicode sequences
    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match: any, hex: any) => {
      escapesCount++;
      return String.fromCharCode(parseInt(hex, 16));
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        escapesFound: escapesCount,
        processingTime: endTime - startTime,
        isValidJson
      }
    };
  }, []);

  const processText = useCallback(() => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeJson : unescapeJson;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeJson, unescapeJson]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: any) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = () => {
    const blob = new Blob([outputText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-json.json`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target && e.target.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(inputText);
      const formatted = JSON.stringify(parsed, null, 2);
      setInputText(formatted);
      showNotification('JSON formatted!');
    } catch (e) {
      showNotification('Invalid JSON format');
    }
  };

  const minifyJson = () => {
    try {
      const parsed = JSON.parse(inputText);
      const minified = JSON.stringify(parsed);
      setInputText(minified);
      showNotification('JSON minified!');
    } catch (e) {
      showNotification('Invalid JSON format');
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(inputText);
      showNotification('✓ Valid JSON');
    } catch (e) {
      showNotification('✗ Invalid JSON: ');
    }
  };

  const clearAll = () => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      escapesFound: 0,
      processingTime: 0,
      operationsCount: 0,
      isValidJson: false
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-blue-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg">
              <Code className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">JSON Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced JSON string processor with validation & analytics</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-green-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape JSON</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape JSON</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.charactersProcessed}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.escapesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-orange-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className={`text-2xl font-bold ${stats.isValidJson ? 'text-green-400' : 'text-red-400'}`}>
                  {stats.isValidJson ? '✓' : '✗'}
                </div>
                <div className="text-gray-300 text-sm">Valid JSON</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <button
                  onClick={validateJson}
                  className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  title="Validate JSON"
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
                <button
                  onClick={formatJson}
                  className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  title="Format JSON"
                >
                  <Code className="w-4 h-4" />
                </button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter JSON string to escape...' : 'Enter escaped JSON to unescape...'}
              className="flex-1 min-h-80 bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none resize-none font-mono text-sm"
            />
            
            <div className="mt-2 text-gray-400 text-sm">
              Characters: {inputText.length}
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed output will appear here..."
              className="flex-1 min-h-80 bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm"
            />
            
            <div className="mt-2 text-gray-400 text-sm">
              Characters: {outputText.length}
            </div>
          </div>
        </div>

        {/* Advanced Options */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setInputText('{"name": "John Doe", "message": "Hello\\nWorld!", "path": "C:\\\\Users\\\\example"}')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample JSON
            </button>
            <button
              onClick={() => setInputText('This is a "quoted" string\\nwith newlines\\tand tabs!')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Text
            </button>
            <button
              onClick={formatJson}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
            >
              Format JSON
            </button>
            <button
              onClick={minifyJson}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors text-sm"
            >
              Minify JSON
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default JsonEscapeUnescapeTool;